/**
 * Strict allowlisted rebuild for question-answer evaluation settings payloads.
 */

const V2_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const REQUEST_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;
const FORBIDDEN_TREE_KEYS = new Set<string>(["canonicalUserId"]);

export type CognitoryQuestionAnswerEvaluationSettingsView = {
  enabled: boolean;
  updatedAt?: string;
};

export type CognitoryQuestionAnswerEvaluationSettingsRebuildResult =
  | {
      kind: "success";
      supportReference: string;
      cognitoryId: string;
      settings: CognitoryQuestionAnswerEvaluationSettingsView;
    }
  | { kind: "malformed_success"; supportReference: string | null };

function isValidRequestId(value: unknown): value is string {
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value);
}

function isValidCognitoryId(value: unknown): boolean {
  return typeof value === "string" && V2_ID_PATTERN.test(value);
}

function isIsoZ(value: string): boolean {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return false;
  }
  return new Date(parsed).toISOString() === value;
}

export function treeContainsForbiddenIdentityKey(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(item => treeContainsForbiddenIdentityKey(item));
  }
  for (const [key, nested] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (FORBIDDEN_TREE_KEYS.has(key)) {
      return true;
    }
    if (treeContainsForbiddenIdentityKey(nested)) {
      return true;
    }
  }
  return false;
}

export function rebuildCognitoryQuestionAnswerEvaluationSettingsPayload(input: {
  expectedCognitoryId: string;
  body: unknown;
}): CognitoryQuestionAnswerEvaluationSettingsRebuildResult {
  if (treeContainsForbiddenIdentityKey(input.body)) {
    return { kind: "malformed_success", supportReference: null };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  if (!isValidRequestId(record.requestId)) {
    return { kind: "malformed_success", supportReference: null };
  }
  const requestId = record.requestId as string;
  if (
    record.decision !== "settings" &&
    record.decision !== "settings_updated"
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (
    !isValidCognitoryId(record.cognitoryId) ||
    record.cognitoryId !== input.expectedCognitoryId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (typeof record.enabled !== "boolean") {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const keys = Object.keys(record).sort();
  if (keys.join(",") === "cognitoryId,decision,enabled,requestId") {
    return {
      kind: "success",
      supportReference: requestId,
      cognitoryId: record.cognitoryId as string,
      settings: { enabled: record.enabled },
    };
  }
  if (keys.join(",") === "cognitoryId,decision,enabled,requestId,updatedAt") {
    const updatedAt = String(record.updatedAt ?? "");
    if (!isIsoZ(updatedAt)) {
      return { kind: "malformed_success", supportReference: requestId };
    }
    return {
      kind: "success",
      supportReference: requestId,
      cognitoryId: record.cognitoryId as string,
      settings: { enabled: record.enabled, updatedAt },
    };
  }
  return { kind: "malformed_success", supportReference: requestId };
}
